'use client'

import CheapestQuickestNewBadge from '@/src/components/common/srp/cheapestQuickestCardLabel/cheapestQuickestNew'
import EmptyFlightScreenWrapper from '@/src/components/common/srp/emptyFlightScreen'
import SelectedFlightFilter from '@/src/components/common/srp/filters/sameAirlineFilterRT'
import NearbyAirportPopup from '@/src/components/common/srp/nearbyAirportPopup'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import FlightDetailsWrapperDesktop from '@/src/components/desktop/srp/flightResults/flightDetails'
import FullPageLoader from '@/src/components/mobile/fullPageLoader'
import { DESKTOP } from '@/src/constants/deviceTypes'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useSrpGtmEvent from '@/src/hooks/analytics/gtm/srp'
import { useGetCheapestQuickestCard } from '@/src/hooks/srp/getCheapestQuickestCard'
import useNearByAirportAlertManager from '@/src/hooks/srp/nearbyAirportAlertManager'
import { useRTSolutionFlowManager } from '@/src/hooks/srp/RTSolutionView/RTSolutionFlowManager'
import { FlightCard } from '@/src/types/flight'
import { Box, Card, Dialog, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import { FC, useCallback, useEffect } from 'react'
import ReactList from 'react-list'
import FareFamilyWrapper from '../fareFamily'
import { Plan } from '../fareFamily/types'
import FlightDetailsSlider from '../flightResults/flightDetails/flightDetails'
import SortTabsDesktop from '../sortTabs'
import { TransitionLoader } from './legTransitionLoader'
import RtSolutionCardWrapper from './rtSolutionCardWrapper'
import RtSolutionCard from './rtSolutionCardWrapper/rtSolutionCard'
import StepTabs from './stepTabs'

interface RTSolutionFlowWrapperProps {
    handleFlightBook?: (cards: FlightCard[], fareFamilyData?: (Plan | null)[]) => void
}

const RTSolutionFlowWrapper: FC<RTSolutionFlowWrapperProps> = ({ handleFlightBook }) => {
    const {
        priceGroupEntries,
        route,
        stepperSteps,
        selectedOnwardCard,
        changeJourneyLegTo,
        handleSelectCard,
        returnLegsTransition,
        handleOpenViewDetails,
        cardDetails,
        handleCloseViewDetails,
        sectors,
        sid,
        jsons,
        showEmptyFlightScreen,
        isFareFamilyLoading,
        fareFamilyLoadingMessage,
        fareFamilyPopup,
        closeFareFamilyPopup,
        handleSelectFareFamilyPlan,
        selectedOnwardFareFamilyPlan,
        selectedJourneyLeg
    } = useRTSolutionFlowManager({ handleFlightBook, deviceType: DESKTOP })

    // Nearby airport alert manager - handles all nearby airport logic internally
    const {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen: nearbyPopupOpen,
        popupData: nearbyPopupData
    } = useNearByAirportAlertManager({
        onContinue: handleSelectCard,
        jsons: jsons!!,
        sectors: sectors!!,
        selectedJourneyLeg,
        handleFlightDetailsClose: handleCloseViewDetails
    })

    const quickestCheapestCard = useGetCheapestQuickestCard()
    const { triggerSrpEvent } = useClevertapForSrp()
    const { pushEventToDataLayer } = useSrpGtmEvent()
    const locale = useLocale()

    useEffect(() => {
        triggerSrpEvent('Air_PageLoad', {
            page_load_time: Math.round(performance.now())
        })
        pushEventToDataLayer({
            event: 'page_view_flight_search_results',
            page_category: 'home'
        })
    }, [])

    const renderFlightCard = useCallback(
        (index: number) => {
            const [groupKey, cards] = priceGroupEntries[index]
            if (quickestCheapestCard?.some(sortOption => sortOption?.card?.id === cards[0].id)) {
                return null
            }

            return (
                <ErrorBoundaryWrapper
                    identifier="RtSolutionPriceGroupsCardWrapper-rtSolutionViewFlow-desktop-srp"
                    fallback={null}
                    key={groupKey}
                >
                    <Box pb={1}>
                        <RtSolutionCardWrapper
                            samePriceCards={cards}
                            onSelectCard={handleFlightSelection}
                            onViewDetailsClick={handleOpenViewDetails}
                        />
                    </Box>
                </ErrorBoundaryWrapper>
            )
        },
        [priceGroupEntries, handleFlightSelection, handleOpenViewDetails, quickestCheapestCard]
    )

    const renderCheapestQuickestCard = useCallback(() => {
        return (
            <Box p={2} borderRadius={1} bgcolor={"#F3F3F3"} mb={1} display={"flex"} flexDirection={"column"} gap={1}>
                {quickestCheapestCard.map(sortOptions => (
                    <CheapestQuickestNewBadge
                        key={sortOptions.id}
                        label={sortOptions.label}
                        isActive={!!sortOptions.isActive}
                        sortOptionId={sortOptions.id}
                        isMobile={false}
                        isCombined={sortOptions.isCombined}
                    >
                        <Card
                            sx={{
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                p: 0,
                                mx: 0,
                                position: 'relative'
                            }}
                        >
                            <Box height={"10px"}></Box>
                            <YellowBannerPromo promos={sortOptions?.card?.promos} />
                            <Box
                                sx={{
                                    px: 3,
                                    pt: (sortOptions?.card?.promos?.length || 0) > 0 ? 2.5 : 1.5,
                                    pb: 1.5
                                }}
                            >
                                <ErrorBoundaryWrapper
                                    identifier={`RtSolutionMainCard-desktop-srp-${sortOptions?.card?.id}`}
                                    fallback={null}
                                >
                                    <RtSolutionCard
                                        card={sortOptions?.card!}
                                        onSelectCard={handleFlightSelection}
                                        onViewDetailsClick={handleOpenViewDetails}
                                    />
                                </ErrorBoundaryWrapper>
                            </Box>
                        </Card>
                    </CheapestQuickestNewBadge>
                ))}
            </Box>
        )
    }, [handleFlightSelection, handleOpenViewDetails, quickestCheapestCard])

    return (
        <>
            {returnLegsTransition && selectedOnwardCard && (
                <TransitionLoader
                    onwardFlightData={selectedOnwardCard}
                    selectedOnwardFareFamilyPlan={selectedOnwardFareFamilyPlan}
                />
            )}

            {/* <ErrorBoundaryWrapper identifier="FlightBookLinearStepper-rtSolutionViewFlow-desktop-srp">
                <FlightBookLinearStepper stepperSteps={stepperSteps} />
            </ErrorBoundaryWrapper> */}

            {showEmptyFlightScreen ? (
                <ErrorBoundaryWrapper identifier="EmptyFlightScreenWrapper-rtSolutionViewFlow-desktop-srp">
                    <EmptyFlightScreenWrapper deviceType={DESKTOP} />
                </ErrorBoundaryWrapper>
            ) : (
                <>
                    {/* <AirlineFilterWrapperTopBar deviceType={DESKTOP} /> */}
                    <ErrorBoundaryWrapper identifier="StepTabs-rtSolutionViewFlow-desktop-srp">
                        <StepTabs
                            onwardFlightData={selectedOnwardCard}
                            onChangeOnwardSelection={() => changeJourneyLegTo(0)}
                            jsons={jsons}
                            selectedJourneyLeg={selectedJourneyLeg}
                        />
                    </ErrorBoundaryWrapper>
                    {/* {selectedOnwardCard && (
                        <ErrorBoundaryWrapper identifier="master-tuple-wrapper-rtSolutionViewFlow-desktop-srp">
                            <MasterTupleWrapper
                                onwardFlightData={selectedOnwardCard}
                                onChangeOnwardSelection={() => changeJourneyLegTo(0)}
                                selectedOnwardFareFamilyPlan={selectedOnwardFareFamilyPlan}
                            />
                        </ErrorBoundaryWrapper>
                    )} */}
                    <Box sx={{ pb: 2, display: 'flex' }}>
                        <ErrorBoundaryWrapper identifier="SortTabWrapper-rtSolutionViewFlow-desktop-srp">
                            <SortTabsDesktop />
                        </ErrorBoundaryWrapper>
                    </Box>

                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff', borderRadius: 1.5 }}>
                        {/* Header */}
                        <Box
                            sx={{
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: 1,
                                borderColor: 'grey.200',
                                pb: 1
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'grey.700'
                                }}
                            >
                                {route}
                            </Typography>

                            {selectedOnwardCard && (
                                <ErrorBoundaryWrapper
                                    fallback={null}
                                    identifier="srp-selected-same-flight-filter-Desktop-RT-solution-flow"
                                >
                                    <SelectedFlightFilter onwardFlightData={selectedOnwardCard} />
                                </ErrorBoundaryWrapper>
                            )}
                            {/* <Button
                                variant="text"
                                size="small"
                                endIcon={<ArrowUpward sx={{ fontSize: 16 }} />}
                                // onClick={onSortByPrice}
                                sx={{
                                    color: 'brand.primary',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        bgcolor: 'transparent'
                                    }
                                }}
                            >
                                {tCommon('price')}
                            </Button> */}
                        </Box>

                        {/* Flight list  */}
                        {renderCheapestQuickestCard()}
                        <Box sx={{ px: 2 }}>

                            <ReactList
                                length={priceGroupEntries.length}
                                threshold={300}
                                itemRenderer={renderFlightCard}
                            />
                        </Box>
                    </Box>
                </>
            )}
            <ErrorBoundaryWrapper identifier="FlightDetailsWrapperDesktop-rtSolutionViewFlow-desktop-srp">
                <FlightDetailsWrapperDesktop open={!!cardDetails} onClose={handleCloseViewDetails}>
                    {cardDetails && (
                        <ErrorBoundaryWrapper identifier="FlightDetailsSlider-rtSolutionViewFlow-desktop-srp">
                            <FlightDetailsSlider
                                onClose={handleCloseViewDetails}
                                flights={cardDetails.cards}
                                selectedFareFamilyPlans={cardDetails.selectedFareFamilyPlans}
                                jsons={jsons!}
                                sectors={sectors!}
                                onSelect={() => handleFlightSelection(cardDetails.cards[1] || cardDetails.cards[0], cardDetails?.cards?.length === 2)}
                                sid={sid!}
                                isRTSolutionView={true}
                                showBaggage={cardDetails.showBaggage}
                            />
                        </ErrorBoundaryWrapper>
                    )}
                </FlightDetailsWrapperDesktop>
            </ErrorBoundaryWrapper>

            <NearbyAirportPopup
                open={nearbyPopupOpen}
                onClose={handleNearbyPopupClose}
                onContinue={handleNearbyPopupContinue}
                data={nearbyPopupData}
            />

            <Dialog
                keepMounted
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                open={!!fareFamilyPopup}
                onClose={closeFareFamilyPopup}
                maxWidth="xl"
                fullWidth
                slotProps={{
                    paper: { sx: { maxWidth: '80rem', margin: 0, minWidth: 'min(100%,1024px)' } },
                    backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }
                }}
            >
                <FareFamilyWrapper
                    flight={fareFamilyPopup?.card}
                    sectors={sectors!}
                    handleClose={closeFareFamilyPopup}
                    handleCreateBooking={handleSelectFareFamilyPlan}
                    jsons={jsons!}
                    desktop={true}
                    rawFareFamilyData={fareFamilyPopup?.fareFamilyData}
                    key={selectedJourneyLeg}
                />
            </Dialog>

            {isFareFamilyLoading && (
                <FullPageLoader
                    message={
                        <Box mt={3} textAlign="center">
                            <Typography variant="h6" fontWeight={700} color="text.secondary">
                                {fareFamilyLoadingMessage}
                            </Typography>
                        </Box>
                    }
                />
            )}
        </>
    )
}

export { RTSolutionFlowWrapper }
export default RTSolutionFlowWrapper
